import React, { useEffect, useRef, useState } from "react";
import { CameraIcon, CircleIcon, Loader2Icon, SquareIcon } from "lucide-react";
interface VideoRecorderProps {
  onRecorded: (file: File) => void;
  maxSeconds?: number;
}
type Status = "idle" | "starting" | "ready" | "recording" | "error";
const fmt = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
export function VideoRecorder({
  onRecorded,
  maxSeconds = 120
}: VideoRecorderProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const [status, setStatus] = useState<Status>("idle");
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState("");
  const stopStream = () => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  };
  useEffect(() => () => {
    if (recorderRef.current?.state === "recording") recorderRef.current.stop();
    stopStream();
  }, []);
  useEffect(() => {
    if (status !== "recording") return;
    const id = window.setInterval(() => setElapsed(e => e + 1), 1000);
    return () => window.clearInterval(id);
  }, [status]);
  useEffect(() => {
    if (status === "recording" && elapsed >= maxSeconds) recorderRef.current?.stop();
  }, [elapsed, maxSeconds, status]);
  const enableCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setStatus("error");
      setError("This browser can't record video. Please use the Upload option instead.");
      return;
    }
    setStatus("starting");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "user",
          width: {
            ideal: 1280
          },
          height: {
            ideal: 720
          }
        },
        audio: true
      });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      setStatus("ready");
    } catch {
      setStatus("error");
      setError("Camera access was blocked. Allow camera and microphone in your browser settings, or upload a video instead.");
    }
  };
  const startRecording = () => {
    const stream = streamRef.current;
    if (!stream) return;
    const mime = ["video/webm;codecs=vp9,opus", "video/webm", "video/mp4"].find(t => MediaRecorder.isTypeSupported(t));
    const recorder = new MediaRecorder(stream, mime ? {
      mimeType: mime
    } : undefined);
    chunksRef.current = [];
    recorder.ondataavailable = e => {
      if (e.data.size) chunksRef.current.push(e.data);
    };
    recorder.onstop = () => {
      const type = recorder.mimeType || "video/webm";
      const blob = new Blob(chunksRef.current, {
        type
      });
      const ext = type.includes("mp4") ? "mp4" : "webm";
      stopStream();
      setStatus("idle");
      setElapsed(0);
      onRecorded(new File([blob], `check-in-${Date.now()}.${ext}`, {
        type
      }));
    };
    recorder.start(1000);
    recorderRef.current = recorder;
    setElapsed(0);
    setStatus("recording");
  };
  const live = status === "ready" || status === "recording";
  return <div data-id="element-715" data-mp="3rbj8kroiam7q">
      <div className="relative aspect-video w-full overflow-hidden rounded-2xl bg-navy" data-id="element-716" data-mp="3rb9il43gof21">
        <video ref={videoRef} autoPlay muted playsInline className={`h-full w-full -scale-x-100 object-cover ${live ? "" : "hidden"}`} data-id="element-717" data-mp="3mceze11l0ohe" />
        {!live && <div className="bg-waves absolute inset-0 flex flex-col items-center justify-center px-6 text-center text-white" data-id="element-718" data-mp="2ulsegtk4n12g">
            {status === "error" ? <p className="max-w-sm text-sm text-white/80" data-id="element-719" data-mp="38ei3l6uycm3s">{error}</p> : <button type="button" onClick={enableCamera} disabled={status === "starting"} className="inline-flex items-center gap-2 rounded-xl bg-bryellow px-5 py-3 text-sm font-bold text-navy transition-[transform,background-color] duration-150 hover:bg-[#ffd633] active:scale-[0.97]" data-id="element-720" data-mp="33jucd08kddli">
                {status === "starting" ? <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-721" data-mp="2tgqng33vr2nd" /> : <CameraIcon className="h-4 w-4" aria-hidden="true" data-id="element-722" data-mp="338wdv8ehhc0g" />}
                Turn on camera
              </button>}
          </div>}
        {status === "recording" && <span className="absolute left-4 top-4 inline-flex items-center gap-2 rounded-md bg-navy/80 px-2.5 py-1 text-sm font-semibold tabular-nums text-white" data-id="element-723" data-mp="3c91mtmzbsigq">
            <span className="h-2 w-2 animate-pulse rounded-full bg-danger" aria-hidden="true" data-id="element-724" data-mp="3h6kz4hcgql61" />
            {fmt(elapsed)} / {fmt(maxSeconds)}
          </span>}
      </div>

      {live && <div className="mt-4 flex justify-center" data-id="element-725" data-mp="1mwpsdywhtavr">
          {status === "ready" ? <button type="button" onClick={startRecording} className="inline-flex items-center gap-2 rounded-full bg-danger px-6 py-3 text-sm font-bold text-white transition-transform duration-150 active:scale-[0.97]" data-id="element-726" data-mp="1rtzmvsyqocqg">
              <CircleIcon className="h-4 w-4 fill-current" aria-hidden="true" data-id="element-727" data-mp="72zs2tt9w0u" />
              Start recording
            </button> : <button type="button" onClick={() => recorderRef.current?.stop()} className="inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-sm font-bold text-white transition-transform duration-150 active:scale-[0.97]" data-id="element-728" data-mp="3rarntjt5vp2k">
              <SquareIcon className="h-4 w-4 fill-current" aria-hidden="true" data-id="element-729" data-mp="iyvpnodl314c" />
              Stop recording
            </button>}
        </div>}
    </div>;
}